import React, { useEffect, useRef, useState } from "react";

export default function ScrollingGallery({ images }) {
  const galleryRef = useRef(null);
  const observerRef = useRef(null);
  const [scrollIndex, setScrollIndex] = useState(0);
  const [isGalleryActive, setIsGalleryActive] = useState(false);

  const [scrolling, setScrolling] = useState(false);

  const [scrollDelta, setScrollDelta] = useState(0);

  useEffect(() => {
    let scrollDelta = 0;
    const SCROLL_THRESHOLD = 100;
  
    const handleScroll = (event) => {
      if (!galleryRef.current || !isGalleryActive) return;
  
      event.preventDefault(); // Always block page scroll while gallery is active
  
      scrollDelta += event.deltaY;
  
      if (scrollDelta > SCROLL_THRESHOLD && scrollIndex < images.length - 1) {
        setScrollIndex((prev) => prev + 1);
        scrollDelta = 0;
      } else if (scrollDelta < -SCROLL_THRESHOLD && scrollIndex > 0) {
        setScrollIndex((prev) => prev - 1);
        scrollDelta = 0;
      }
  
      // Optional: allow exit if trying to scroll beyond gallery
      if (scrollIndex === images.length - 1 && scrollDelta > SCROLL_THRESHOLD) {
        setIsGalleryActive(false);
      }
      if (scrollIndex === 0 && scrollDelta < -SCROLL_THRESHOLD) {
        setIsGalleryActive(false);
      }
    };
  
    if (isGalleryActive) {
      window.addEventListener("wheel", handleScroll, { passive: false });
    }
  
    return () => {
      window.removeEventListener("wheel", handleScroll);
    };
  }, [scrollIndex, isGalleryActive, images.length]);
  
  

  // Ensure gallery is fully visible before activating scrolling
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio === 1) {
          setIsGalleryActive(true);
        } else {
          setIsGalleryActive(false);
        }
      },
      { threshold: 1 } // 100% visibility required
    );

    if (galleryRef.current) {
      observerRef.current.observe(galleryRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return (
    <>
      <div className="gallery-container" ref={galleryRef}>
        <div
          className="gallery-wrapper"
          style={{
            width: `${images.length * 100}%`, // <-- Correct dynamic width
            transform: `translateX(calc(-${scrollIndex * (100)}% - ${scrollIndex * 140}px))`,

            transition: "transform 1.5s ease-in-out",
          }}
        >

        
{images.map((src, index) => {
  const isActive = index === scrollIndex;

  return (
    <div
      key={index}
      className="gallery-slide"
      style={{
        backgroundImage: `url(${src})`,
        width: `${100 / images.length}%`,
        opacity: isActive ? 1 : 0,
        transform: isActive ? "scale(1)" : "scale(0.85)",
        transition: isActive
          ? "none"
          : "opacity 1.5s cubic-bezier(0.25, 1, 0.5, 1), transform 1.5s cubic-bezier(0.25, 1, 0.5, 1)",
        pointerEvents: isActive ? "auto" : "none",
      }}
    />
  );
})}

        </div>
      </div>

      <style>
        {`
          .gallery-container {
            width: 80vw; /* Ensures one image fills the container */
            height: 80vh;
            margin: auto;
            display: flex;
            justify-content: center;
            align-items: center;
            overflow: hidden;
            position: relative;
          }

          .gallery-wrapper {
            display: flex;
            width: calc(100% * IMAGE_COUNT); /* dynamically set in React or use inline style */
            height: 100%;
            scroll-snap-type: x mandatory; /* Enables smooth snapping */
            gap: 140px;
            }
          
          .gallery-slide {
            width: calc(100% / IMAGE_COUNT); /* ← again, this won't work as CSS */
            flex: 0 0 100%; /* take up full width of the container */
            height: 100%;
            background-position: center;
            background-repeat: no-repeat;
            background-size: cover;
            border-radius: 20px;
            scroll-snap-align: center;
          }
        `}
      </style>
    </>
  );
}
